import { InviteStatus, type UpdateEmployeeParams } from "@pentacode/core";
import { html, LitElement } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { alert, confirm } from "../../../elements/alert-dialog";
import { app } from "../../../init";
import { routeProperty, Routing } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";
import "../components/floating-form-buttons";
import "./components/employees-profile-form";
import type { ProfileForm } from "./components/employees-profile-form";

@customElement("ptc-employees-core-data-single")
export class EmployeesCoreDataSingle extends Routing(StateMixin(LitElement)) {
    routePattern = /^employees\/(?<employeeId>\d+)\/core-data?/;

    get routeTitle() {
        return `Stammdaten: ${this._employee && this._employee.name}`;
    }

    @routeProperty({ type: Number, arg: "employeeId" })
    private _employeeId: number;

    @query("ptc-profile-form")
    private _profileForm: ProfileForm;

    @state()
    private _loading: boolean = false;

    private get _employee() {
        return app.getEmployee(this._employeeId)!;
    }

    private get _profileData() {
        return this._profileForm?.data || {};
    }
    private get _hasProfileChanged() {
        return this._profileForm?.hasChanged;
    }

    static styles = [shared];

    async updated(changes: Map<string, unknown>) {
        if (!changes.has("_employeeId") && !changes.has("_page")) {
            return;
        }

        void this._resetProfile();
    }

    private async _resetProfile() {
        await this._profileForm.init(this._employee);
        this.requestUpdate();
    }

    private async _submitProfile() {
        if (!this._profileForm.reportValidity()) {
            return;
        }

        const data = this._profileData;

        if (data.email !== this._employee.email) {
            if (
                !!this._employee.accountId &&
                !(await confirm(
                    html`Dieser Mitarbeiter besitzt einen Mitarbeiterzugang mit der Email
                        <strong>${this._employee.email}</strong> Wollen Sie die Emailadresse trotzdem auf
                        <strong>${data.email}</strong> abändern? Der Mitarbeiterzugang wird dann zurückgesetzt und muss
                        erneut eingerichtet werden.`,
                    "Bestätigen",
                    "Abbrechen",
                    { title: "Emailadresse Bearbeiten" }
                ))
            ) {
                return;
            } else if (
                this._employee.invite?.status === InviteStatus.Sent &&
                !(await confirm(
                    html`Es wurde bereits eine Einladung zur Einrichtung eines Mitarbeiterzugangs an die Emailadresse
                        <strong>${this._employee.email}</strong> versandt. Wollen Sie die Emailadresse trotzdem auf
                        <strong>${data.email}</strong> abändern? Die Einladung muss dann erneut versendet werden.`,
                    "Bestätigen",
                    "Abbrechen",
                    { title: "Emailadresse Bearbeiten" }
                ))
            ) {
                return;
            }
        }

        await this._update(data);
    }

    private async _update(data: Partial<UpdateEmployeeParams>, rethrowErrors = false, refresh = true) {
        this._loading = true;
        try {
            await app.updateEmployee(this._employee!, data, refresh);
            this._loading = false;
        } catch (e) {
            this._loading = false;
            if (rethrowErrors) {
                throw e;
            } else {
                void alert(e.message, { type: "warning" });
            }
        }

        void this.updated(new Map<string, unknown>([["_employeeId", this._employeeId]]));
    }

    render() {
        return html`<div class="core_data fullbleed">
                <div class="vertical layout fit-vertically">
                    <ptc-scroller class="stretch">
                        <ptc-profile-form
                            @submit=${this._submitProfile}
                            @input=${() => this.requestUpdate()}
                            @change=${() => this.requestUpdate()}
                            class="double-padded narrow-content"
                        ></ptc-profile-form>
                    </ptc-scroller>

                    <ptc-floating-form-buttons
                        ?hidden=${!this._hasProfileChanged}
                        ?disabled=${this._loading}
                        @cancel=${this._resetProfile}
                        @save=${this._submitProfile}
                    ></ptc-floating-form-buttons>
                </div>
            </div>
            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>`;
    }
}
